import { computed, ref, watch } from "vue";
import { defineStore } from "pinia";
import { PaymentMethod } from "@smeetz/orders";
import { useOrderStore } from "@smeetz/order-store";
import { BookingStep } from "@/constants/booking";
import { Currency } from "@/constants/currency";
import { LogService } from "@/services/logging";

interface OrderMeta {
  step: BookingStep;
  paymentMethod: PaymentMethod;
  currency: Currency | null;
  providedAmount: number;
  /**
   * Price IDs selected in DiscountSelectTickets modal — used by Discount.vue's
   * Apply to scope the manual discount to specific lines. Empty = all order.
   */
  discountPriceIds: string[];
}

export const useOrderMetaStore = defineStore("orderMeta", () => {
  const ordersMeta = ref<Record<string, Partial<OrderMeta>>>({});
  const refundMethod = ref<PaymentMethod>(PaymentMethod.CASH);

  const defaultPaymentMethod = PaymentMethod.CASH;
  const defaultStep = BookingStep.Order;

  const orderStore = useOrderStore();

  const step = computed(() => {
    const orderId = orderStore.selectedOrder?.id;
    return orderId
      ? ordersMeta.value[orderId]?.step ?? defaultStep
      : defaultStep;
  });

  const paymentMethod = computed(() => {
    const orderId = orderStore.selectedOrder?.id;
    return orderId
      ? ordersMeta.value[orderId]?.paymentMethod
      : defaultPaymentMethod;
  });

  const currency = computed(() => {
    const orderId = orderStore.selectedOrder?.id;
    return orderId ? ordersMeta.value[orderId]?.currency : null;
  });

  const providedAmount = computed(() => {
    const orderId = orderStore.selectedOrder?.id;
    if (!orderId) return 0;

    const meta = ordersMeta.value[orderId];
    if (meta?.providedAmount !== undefined && meta?.providedAmount !== null) {
      return meta.providedAmount;
    }

    return orderStore.selectedOrderSummary.balance;
  });

  const isInOrderStep = computed(() => {
    return step.value === BookingStep.Order;
  });

  const isInPaymentStep = computed(() => {
    return [
      BookingStep.Payment,
      BookingStep.Discount,
      BookingStep.Promocode,
      BookingStep.PromocodeCampaigns,
      BookingStep.Offers,
    ].includes(step.value);
  });

  const isInCompletedStep = computed(() => {
    return step.value === BookingStep.CompleteOrder;
  });

  watch(
    () => orderStore.selectedOrderSummary.balance,
    (totalBalance) => {
      if (totalBalance !== undefined) {
        setProvidedAmount(totalBalance);
      }
    }
  );

  function setStep(newStep: BookingStep) {
    const orderId = orderStore.selectedOrder?.id;
    if (orderId) {
      if (!ordersMeta.value[orderId]) ordersMeta.value[orderId] = {};
      const previousStep = ordersMeta.value[orderId].step;
      if (previousStep !== newStep) {
        LogService.info(
          `Order meta: step ${
            previousStep ?? defaultStep
          } -> ${newStep} on order ${orderStore.selectedOrder?.reference}`,
          { orderId }
        );
      }
      ordersMeta.value[orderId].step = newStep;
    }
  }

  function setPaymentMethod(method: PaymentMethod) {
    const orderId = orderStore.selectedOrder?.id;
    if (orderId) {
      if (!ordersMeta.value[orderId]) ordersMeta.value[orderId] = {};
      if (ordersMeta.value[orderId].paymentMethod !== method) {
        LogService.info(
          `Order meta: payment method set to ${method} on order ${orderStore.selectedOrder?.reference}`,
          { orderId }
        );
      }
      ordersMeta.value[orderId].paymentMethod = method;
    }
  }

  function setCurrency(curr: Currency | null) {
    const orderId = orderStore.selectedOrder?.id;
    if (orderId) {
      if (!ordersMeta.value[orderId]) ordersMeta.value[orderId] = {};
      if (ordersMeta.value[orderId].currency?.label !== curr?.label) {
        LogService.info(
          `Order meta: pay currency set to ${
            curr?.label ?? "order currency"
          } on order ${orderStore.selectedOrder?.reference}`,
          { orderId, exchangeRate: curr?.exchangeRate }
        );
      }
      ordersMeta.value[orderId].currency = curr;
    }
  }

  function setProvidedAmount(amount: number) {
    const orderId = orderStore.selectedOrder?.id;
    if (orderId) {
      if (!ordersMeta.value[orderId]) ordersMeta.value[orderId] = {};
      // Journal only deviations from the balance: reactive syncs (page mount,
      // the balance watcher above) set the amount TO the balance, while a
      // cashier intervention (calculator entry, converted currency amount)
      // sets something else — that's the signal behind partial payments.
      const balance = orderStore.selectedOrderSummary?.balance;
      if (
        amount !== balance &&
        ordersMeta.value[orderId].providedAmount !== amount
      ) {
        LogService.info(
          `Order meta: payment amount set to ${amount} on order ${orderStore.selectedOrder?.reference}`,
          {
            orderId,
            amount,
            balance,
            currency: ordersMeta.value[orderId].currency?.label,
            exchangeRate: ordersMeta.value[orderId].currency?.exchangeRate,
          }
        );
      }
      ordersMeta.value[orderId].providedAmount = amount;
    }
  }

  const discountPriceIds = computed<string[]>(() => {
    const orderId = orderStore.selectedOrder?.id;
    return orderId ? ordersMeta.value[orderId]?.discountPriceIds ?? [] : [];
  });

  function setDiscountPriceIds(priceIds: string[]) {
    const orderId = orderStore.selectedOrder?.id;
    if (orderId) {
      if (!ordersMeta.value[orderId]) ordersMeta.value[orderId] = {};
      ordersMeta.value[orderId].discountPriceIds = [...priceIds];
    }
  }

  function clearDiscountPriceIds() {
    const orderId = orderStore.selectedOrder?.id;
    if (orderId && ordersMeta.value[orderId]) {
      ordersMeta.value[orderId].discountPriceIds = [];
    }
  }

  /**
   * Removes the metadata for a given order reference.
   * This should be called when an order is removed
   */
  function clearOrderMeta(orderId: string) {
    if (ordersMeta.value[orderId]) {
      delete ordersMeta.value[orderId];
    }
  }

  /**
   * Removes the metadata of every order. Called when all orders are cleared
   * (e.g. on cashfloat close).
   */
  function clearAllOrdersMeta() {
    ordersMeta.value = {};
  }

  /**
   * Monotonic counter behind the default tab names ("Order #N") — each new
   * order consumes a unique number for the session, so two unnamed tabs never
   * share a name and numbering doesn't shift as tabs open/close.
   */
  const tabNameCounter = ref(0);

  function nextTabNumber(): number {
    tabNameCounter.value += 1;
    return tabNameCounter.value;
  }

  /**
   * Restart the default tab numbering at "Order #1". Called when the last
   * order is removed, so the fresh auto-created tab doesn't continue the old
   * sequence.
   */
  function resetTabCounter() {
    tabNameCounter.value = 0;
  }

  /**
   * Recently used order tab names, newest first (LIFO, deduplicated, max 4).
   * Global across orders — offered as quick picks in the tab-rename modal.
   */
  const recentTabNames = ref<string[]>([]);

  function addRecentTabName(name: string) {
    const maxTabNames = 4;
    if (recentTabNames.value.includes(name)) {
      return;
    }
    recentTabNames.value.unshift(name);
    if (recentTabNames.value.length > maxTabNames) {
      recentTabNames.value = recentTabNames.value.slice(0, maxTabNames);
    }
  }

  return {
    ordersMeta,
    step,
    setStep,
    paymentMethod,
    refundMethod,
    setPaymentMethod,
    currency,
    setCurrency,
    providedAmount,
    setProvidedAmount,
    discountPriceIds,
    setDiscountPriceIds,
    clearDiscountPriceIds,
    isInOrderStep,
    isInPaymentStep,
    isInCompletedStep,
    clearOrderMeta,
    clearAllOrdersMeta,
    recentTabNames,
    addRecentTabName,
    nextTabNumber,
    resetTabCounter,
    tabNameCounter,
  };
});
